import { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useRef, useMemo } from 'preact/hooks';
import { Rail } from '@accedo/ctv-ui-rail';
import { LabelCard } from '@accedo/ctv-ui-card';
import { Nav } from '@accedo/spatial-nav';
import { Button as CtvButton } from '@accedo/ctv-ui-button';
import { useLanguageDirection, LTR, RTL } from '@accedo/language-utils';
import { AnimationStyle } from '@accedo/virtual-scroll';
import { logger } from '@accedo/logger';
import { usePageNavigation } from '@accedo/page-navigation';
import { ServiceManager } from '##/services/elevate/manager';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator';
import { nav } from '##/services/navigation';

import { getHomePath } from '##/services/routing';
import { User } from '##/dataModels/user';
import { useMenu } from '##/context/MenuContext';
import { getDeviceInfo } from '##/utils/deviceUtils';
import styles from './TestPage.module.scss';

const pageId = 'TestPage';
const LP = '[TestPage]';

/*
Use this page for whatever you need to during dev
*/
export const TestPage: FunctionalComponent = () => {
  const { navigateTo, path } = usePageNavigation();
  const { dir, setLanguageDirection } = useLanguageDirection();
  const railRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<User | undefined>(undefined);
  useMenu(true);

  const items = useMemo(
    () =>
      Array.from({ length: 16 }).map((_, index) => (
        <LabelCard key={`card${index}`} id={`card${index}`} index={index} size="small" cardTitle={`card${index}`} />
      )),
    [],
  );

  const toggleLangDirection = useCallback((): void => {
    if (dir === LTR) {
      setLanguageDirection(RTL);
    } else {
      setLanguageDirection(LTR);
    }
  }, [dir, setLanguageDirection]);

  const goHome = (): void => {
    const homePath = getHomePath();
    if (path !== homePath) {
      navigateTo(homePath);
    }
  };

  const testSearch = useCallback(async (): Promise<void> => {
    const keyword = 'flash';
    setLoadingIndicatorVisibility(true);
    const results = await ServiceManager.getOvpService().search(keyword, {
      itemTypes: ['movie', 'tvshow'],
    });
    logger.debug('Search results for keyword:', keyword, results);
    setLoadingIndicatorVisibility(false);
  }, []);

  const testGetTVShows = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    const results = await ServiceManager.getOvpService().getShows({
      pageSize: 10,
    });
    logger.debug('TV Shows:', results);
    setLoadingIndicatorVisibility(false);
  }, []);

  const testGetMovies = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    const results = await ServiceManager.getOvpService().getMovies({
      pageSize: 10,
    });
    logger.debug('Movies:', results);
    setLoadingIndicatorVisibility(false);
  }, []);

  const testLogin = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    try {
      const deviceDetails = await getDeviceInfo();
      const user = await ServiceManager.getIDPService()?.login({
        username: 'testuser02',
        password: 'password',
        email: 'email',
        deviceDetails,
      });
      userRef.current = user;
      logger.debug('User:', user);
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setLoadingIndicatorVisibility(false);
    }
  }, []);

  const testLogout = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    try {
      if (!userRef.current) {
        await testLogin();
      }
      const token = userRef.current?.authToken?.token;
      if (token) {
        await ServiceManager.getIDPService()?.logout({
          token,
        });
      }
      userRef.current = undefined;
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setLoadingIndicatorVisibility(false);
    }
  }, []);

  const testRegister = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    try {
      const user = await ServiceManager.getIDPService()?.register({
        username: 'testuser02',
        password: 'password',
        email: 'email',
      });
      logger.debug('User:', user);
      userRef.current = user;
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setLoadingIndicatorVisibility(false);
    }
  }, []);

  const testRefreshUserToken = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    try {
      if (!userRef.current) {
        await testLogin();
      }
      if (userRef.current && userRef.current.refreshToken) {
        const user = await ServiceManager.getIDPService()?.refresh(userRef.current?.refreshToken);
        logger.debug('User:', user);
      }
    } catch (error) {
      logger.error('Error:', error);
    } finally {
      setLoadingIndicatorVisibility(false);
    }
  }, []);

  const testGetLanguages = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    // TODO: I18nService removed - NZOS app uses static translations
    logger.warn('I18nService removed - NZOS app uses static translations');
    setLoadingIndicatorVisibility(false);
  }, []);

  const testGetTranslation = useCallback(async (): Promise<void> => {
    setLoadingIndicatorVisibility(true);
    // TODO: I18nService removed - NZOS app uses static translations
    logger.warn('I18nService removed - NZOS app uses static translations');
    setLoadingIndicatorVisibility(false);
  }, []);

  useEffect(() => {
    logger.debug(`${LP} useEffect - no dependencies`);
    nav.assignFocus(pageId);
    setLoadingIndicatorVisibility(false);
  }, []);

  return (
    <Nav.Vertical id={pageId}>
      <div className={styles.testPage} id={pageId}>
        <span>Test Page</span>
        <Nav.Horizontal id={'testNavigationButtons'}>
          <div id={'testNavigationButtons'} className={styles.testButtons}>
            <CtvButton buttonType="primary" id="homeButton" onSelect={goHome}>
              Home
            </CtvButton>
          </div>
        </Nav.Horizontal>
        <Nav.Horizontal id={'testOVPServiceButtons'}>
          <div id={'testOVPServiceButtons'} className={styles.testButtons}>
            <CtvButton buttonType="secondary" id="testSearchService" onSelect={testSearch}>
              Test Search
            </CtvButton>

            <CtvButton buttonType="secondary" id="testGetTVSHows" onSelect={testGetTVShows}>
              Test Get TV Shows
            </CtvButton>
            <CtvButton buttonType="secondary" id="testGetMovies" onSelect={testGetMovies}>
              Test Get Movies
            </CtvButton>
          </div>
        </Nav.Horizontal>
        <Nav.Horizontal id={'testIDPServiceButtons'}>
          <div id={'testIDPServiceButtons'} className={styles.testButtons}>
            <CtvButton buttonType="secondary" id="testLogin" onSelect={testLogin}>
              Test Login
            </CtvButton>
            <CtvButton buttonType="secondary" id="testLogout" onSelect={testLogout}>
              Test Logout
            </CtvButton>
            <CtvButton buttonType="secondary" id="testRegister" onSelect={testRegister}>
              Test Register user
            </CtvButton>
            <CtvButton buttonType="secondary" id="testRefreshUserToken" onSelect={testRefreshUserToken}>
              Test Refresh Token
            </CtvButton>
          </div>
        </Nav.Horizontal>

        <Nav.Horizontal id={'testLanguageRelatedButtons'}>
          <div id={'testLanguageRelatedButtons'} className={styles.testButtons}>
            <CtvButton buttonType="secondary" id="getLanguagesButton" onSelect={testGetLanguages}>
              Get Languages
            </CtvButton>

            <CtvButton buttonType="secondary" id="getTranslationButton" onSelect={testGetTranslation}>
              Get Translation
            </CtvButton>

            <CtvButton
              buttonType="tertiary"
              id="languageDirButton"
              onSelect={toggleLangDirection}
              styling={{ className: `${styles.buttonOverride}` }}
            >
              Toggle Lang Direction (current: {dir})
            </CtvButton>
          </div>
        </Nav.Horizontal>
        <Rail
          ref={railRef}
          railType={'label'}
          id={'rail1'}
          railTitle={'My Cool Title'}
          orientation={'horizontal'}
          scrolling={{
            initialScrollIndex: 10,
            scrollEndBoundary: 3,
            animate: true,
            scrollSpeed: 400,
            scrollTransition: 'ease',
            animationStyle: AnimationStyle.Translate2d,
          }}
          lazyLoading={{
            lazyLoadBoundary: 7,
            lazyLoadBoundaryNegative: 3,
          }}
          scrollImplementation="lazy"
        >
          {items}
        </Rail>

        {/* <div>{JSON.stringify(config)}</div> */}
      </div>
    </Nav.Vertical>
  );
};
